import { ArrowLeft, MapPin, Pencil, UserRound } from 'lucide-react'
import { useEffect, useState } from 'react'
import { NavLink, useParams } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { PageTitle } from '../../components/ui/PageTitle'
import { api } from '../../lib/api'

type Customer = {
  id: number
  name: string
  mobile: string
  email: string
  gender: string
  profileImage?: string | null
  status: string
  joined: string
}

type Address = {
  id: string
  label: string
  recipient: string
  mobile: string
  line1: string
  line2: string
  city: string
  state: string
  pincode: string
  isDefault?: boolean
}

function formatDate(value: string) {
  if (!value) return '-'
  return new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(value))
}

function genderAvatar(gender: string) {
  if (gender === 'Female') return { label: 'F', className: 'bg-rose-100 text-rose-700' }
  if (gender === 'Male') return { label: 'M', className: 'bg-sky-100 text-sky-700' }
  return { label: 'U', className: 'bg-leaf/10 text-leaf-dark' }
}

export function CustomerDetailPage() {
  const { id } = useParams()
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [addresses, setAddresses] = useState<Address[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    api.get(`/admin/customers/${id}`)
      .then(({ data }) => {
        setCustomer(data.customer)
        setAddresses(data.addresses || [])
      })
      .catch(() => setError('Failed to load customer'))
      .finally(() => setLoading(false))
  }, [id])

  if (loading) return <div className="py-24 text-center text-stone-400">Loading customer…</div>
  if (error || !customer) return <div className="py-24 text-center text-red-600">{error || 'Customer not found'}</div>
  const avatar = genderAvatar(customer.gender)

  return (
    <div className="space-y-6">
      <PageTitle
        title={customer.name}
        subtitle="Customer account details."
        action={(
          <div className="flex gap-2">
            <NavLink to="/customers"><Button icon={ArrowLeft} variant="soft">Back to Customers</Button></NavLink>
            <NavLink to={`/customers/${id}/edit`}><Button icon={Pencil}>Edit Customer</Button></NavLink>
          </div>
        )}
      />
      <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-leaf/10 text-leaf-dark">
            <UserRound className="h-5 w-5" />
          </div>
          <h2 className="font-bold text-leaf-dark">Customer Details</h2>
        </div>
        <div className="flex flex-col md:flex-row gap-6 items-start">
          <div className="shrink-0">
            {customer.profileImage ? (
              <img
                src={customer.profileImage}
                alt="Customer profile"
                className="h-32 w-32 rounded-xl object-cover"
              />
            ) : (
              <div className={`grid h-32 w-32 place-items-center rounded-xl text-5xl font-bold ${avatar.className}`}>
                {avatar.label}
              </div>
            )}
          </div>
          <div className="flex-1 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 w-full">
            <div className="rounded-lg bg-stone-50 px-3 py-2 text-sm text-stone-700">
              <span className="block text-xs text-stone-500 mb-1">Full Name</span>
              <span className="font-medium">{customer.name}</span>
            </div>
            <div className="rounded-lg bg-stone-50 px-3 py-2 text-sm text-stone-700">
              <span className="block text-xs text-stone-500 mb-1">Mobile number</span>
              <span className="font-medium">{customer.mobile}</span>
            </div>
            <div className="rounded-lg bg-stone-50 px-3 py-2 text-sm text-stone-700">
              <span className="block text-xs text-stone-500 mb-1">Email</span>
              <span className="font-medium">{customer.email || '-'}</span>
            </div>
            <div className="rounded-lg bg-stone-50 px-3 py-2 text-sm text-stone-700">
              <span className="block text-xs text-stone-500 mb-1">Gender</span>
              <span className="font-medium">{customer.gender}</span>
            </div>
            <div className="rounded-lg bg-stone-50 px-3 py-2 text-sm text-stone-700">
              <span className="block text-xs text-stone-500 mb-1">Status</span>
              <span className="font-medium">{customer.status}</span>
            </div>
            <div className="rounded-lg bg-stone-50 px-3 py-2 text-sm text-stone-700">
              <span className="block text-xs text-stone-500 mb-1">Joined</span>
              <span className="font-medium">{formatDate(customer.joined)}</span>
            </div>
          </div>
        </div>
      </section>
      <section className="rounded-xl border border-stone-200 bg-white p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-leaf/10 text-leaf-dark">
            <MapPin className="h-5 w-5" />
          </div>
          <h2 className="font-bold text-leaf-dark">Customer Addresses</h2>
        </div>
        {addresses.length === 0 ? (
          <p className="mt-4 rounded-lg bg-stone-50 px-3 py-2 text-sm text-stone-600">No saved addresses.</p>
        ) : (
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            {addresses.map(address => (
              <div className="rounded-lg border border-stone-100 bg-stone-50 p-4 text-sm text-stone-700" key={address.id}>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <span className="font-bold text-leaf-dark">{address.label}</span>
                  {address.isDefault ? <span className="rounded-full bg-mango-yellow/40 px-2 py-1 text-xs font-semibold text-leaf-dark">Default</span> : null}
                </div>
                <p className="font-semibold text-stone-900">{address.recipient}</p>
                <p>{address.mobile}</p>
                <p>{address.line1}</p>
                <p>{address.line2}</p>
                <p>{address.city}, {address.state} - {address.pincode}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
